/**
 * switch_environment tool — session-scoped environment switching.
 *
 * The wizard (and an org.json pin) own the DURABLE default environment; this
 * tool lets a conversation visit another environment without leaving the chat
 * or touching configuration. It rebuilds the environment-bound API clients on
 * the live ToolContext — including re-resolving the Dataverse instance URL,
 * which is per-environment; without that, Dataverse tools would silently keep
 * talking to the previous environment's org.
 *
 * Scope semantics: the mutation lands on the ToolContext object the MCP
 * session dispatches through, so in HTTP mode each session switches
 * independently and in stdio mode the switch lasts until the server restarts.
 * Nothing is ever written to config.json — a restart returns to the default.
 *
 * MCP resources (not tools) read a separate shared context and deliberately
 * stay on the boot default.
 */

import { z } from "zod";
import { FlowManagementApi } from "../api/flow-management.js";
import { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { PowerAppsApi } from "../api/powerapps-api.js";
import { PowerPlatformAdminApi } from "../api/power-platform-admin-api.js";
import { ConnectionsApi } from "../api/connections-api.js";
import { PowerPagesApi } from "../api/powerpages-api.js";
import { CopilotEvaluationApi } from "../api/copilot-evaluation-api.js";
import { CopilotStudioExecutionApi } from "../api/copilot-studio-execution-api.js";
import { CopilotAdminApi } from "../api/copilot-admin-api.js";
import { DataverseApi } from "../api/dataverse-api.js";
import { ModelDrivenApi } from "../api/model-driven-api.js";
import { DesktopFlowsApi } from "../api/desktop-flows-api.js";
import { findDataverseInstanceUrl } from "../api/dataverse-discovery.js";
import type { Environment } from "../api/types.js";
import type { AuthProvider } from "../auth/types.js";
import type { FeatureId } from "../config/features.js";
import { hasFeature } from "../config/features.js";
import { ValidationError } from "../utils/errors.js";
import { logger } from "../utils/logger.js";

/** Per-environment settings carried over from config.json, keyed by canonical id. */
export interface EnvironmentConfigOverride {
  dataverseUrl?: string;
  region?: string;
}

/**
 * Session environment state, held on the ToolContext. `currentId !== bootId`
 * is the "switched" signal the destructive-tool banner keys on.
 */
export interface EnvironmentSession {
  bootId: string;
  bootName: string;
  currentId: string;
  currentName: string;
  dataverseUrl: string | null;
  configOverrides: Record<string, EnvironmentConfigOverride>;
}

/**
 * The slice of ToolContext this tool needs. Declared structurally (rather
 * than importing ToolContext) so index.ts can import this module without a
 * cycle; ToolContext satisfies it by construction.
 */
export interface SwitchEnvironmentContext {
  authProvider?: AuthProvider;
  environmentSession?: EnvironmentSession;
  flowApi: FlowManagementApi;
  connectorApi: ConnectorMetadataApi | null;
  powerAppsApi: PowerAppsApi | null;
  adminApi: PowerPlatformAdminApi | null;
  connectionsApi?: ConnectionsApi | null;
  powerPagesApi: PowerPagesApi | null;
  copilotEvaluationApi?: CopilotEvaluationApi | null;
  copilotStudioExecutionApi?: CopilotStudioExecutionApi | null;
  copilotAdminApi?: CopilotAdminApi | null;
  dataverseApi: DataverseApi | null;
  modelDrivenApi?: ModelDrivenApi | null;
  desktopFlowsApi?: DesktopFlowsApi | null;
  defaultEnvironmentId: string;
  enabledFeatures?: FeatureId[];
}

export const switchEnvironmentInputSchema = z.object({
  environment: z
    .string()
    .min(1)
    .describe(
      "Environment ID (GUID or Default-<guid>) or display name, as shown by list_environments",
    ),
});

export const switchEnvironmentTool = {
  name: "switch_environment",
  description:
    "Switch which Power Platform environment this session works in. Takes an environment ID or display name " +
    "(use list_environments to see them). The switch lasts for THIS session only — it never changes the saved " +
    "configuration, and restarting the server returns to the default environment. Dataverse tools are re-pointed " +
    "at the new environment's org automatically. Access is always limited to what the signed-in user already has " +
    "in that environment.",
  inputSchema: {
    type: "object" as const,
    properties: {
      environment: {
        type: "string",
        description:
          "Environment ID (GUID or Default-<guid>) or display name, as shown by list_environments",
      },
    },
    required: ["environment"],
  },
};

/** Mirrors dataverse-discovery's canonical form: bare lowercase GUID. */
function canonicalId(value: string): string {
  return value
    .split("/")
    .at(-1)!
    .toLowerCase()
    .replace(/^default-/, "");
}

export function matchEnvironment(
  environments: readonly Environment[],
  requested: string,
): Environment {
  const wanted = requested.trim();
  const wantedCanonical = canonicalId(wanted);

  const byId = environments.filter((env) => canonicalId(env.name) === wantedCanonical);
  if (byId.length === 1) return byId[0]!;

  const byName = environments.filter(
    (env) => env.properties.displayName?.toLowerCase() === wanted.toLowerCase(),
  );
  if (byName.length === 1) return byName[0]!;
  if (byName.length > 1) {
    const ids = byName.map((env) => `${env.properties.displayName} (${env.name})`).join(", ");
    throw new ValidationError(
      `Multiple environments are named "${wanted}": ${ids}. Switch by environment ID instead.`,
    );
  }

  const available = environments
    .map((env) => `  ${env.properties.displayName} — ${env.name}`)
    .join("\n");
  throw new ValidationError(
    `No accessible environment matches "${wanted}". Environments you can access:\n${available}`,
  );
}

/**
 * Resolve the Dataverse instance URL for the target environment, same
 * precedence as server startup: config override → the environment list we
 * already fetched → BAP admin detail → region-based guess (last resort).
 */
async function resolveDataverseUrl(
  context: SwitchEnvironmentContext,
  environments: readonly Environment[],
  target: Environment,
): Promise<{ url: string | undefined; source: string }> {
  const override = context.environmentSession?.configOverrides[canonicalId(target.name)];
  if (override?.dataverseUrl) {
    return { url: override.dataverseUrl, source: "config" };
  }

  const fromList = findDataverseInstanceUrl(environments, target.name);
  if (fromList) {
    return { url: fromList, source: "environment-list" };
  }

  if (context.adminApi) {
    try {
      const detail = await context.adminApi.getEnvironment(target.name);
      const instanceUrl = detail.properties?.linkedEnvironmentMetadata?.instanceUrl;
      if (instanceUrl) {
        return { url: instanceUrl, source: "bap" };
      }
    } catch (err) {
      logger.debug(
        { err: err instanceof Error ? err.message : String(err) },
        "BAP environment detail unavailable during switch — falling back to region guess",
      );
    }
  }

  const region = override?.region ?? target.properties.azureRegionHint;
  return {
    url: DataverseApi.getDataverseUrl(target.name, region),
    source: "region-guess",
  };
}

export async function handleSwitchEnvironment(
  context: SwitchEnvironmentContext,
  input: unknown,
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const { environment: requested } = switchEnvironmentInputSchema.parse(input);

  const session = context.environmentSession;
  const authProvider = context.authProvider;
  if (!session || !authProvider) {
    throw new ValidationError(
      "Environment switching is not available in this session — the server was started without session-switch support.",
    );
  }

  const environments = await context.flowApi.listEnvironments();
  const target = matchEnvironment(environments, requested);
  const targetId = target.name;
  const targetName = target.properties.displayName ?? targetId;
  const returningToDefault = canonicalId(targetId) === canonicalId(session.bootId);

  // Rebuild every environment-bound client. Null-ness encodes the feature
  // gating decided at startup, so a client that was never constructed stays
  // absent. GraphApi (OneDrive/Excel) and the schema cache are not
  // environment-bound and are left alone.
  context.flowApi = new FlowManagementApi({ authProvider, defaultEnvironmentId: targetId });
  if (context.connectorApi) {
    context.connectorApi = new ConnectorMetadataApi({ authProvider, environmentId: targetId });
  }
  if (context.powerAppsApi) {
    context.powerAppsApi = new PowerAppsApi({ authProvider, defaultEnvironmentId: targetId });
  }
  if (context.adminApi) {
    context.adminApi = new PowerPlatformAdminApi({ authProvider, defaultEnvironmentId: targetId });
  }
  if (context.connectionsApi) {
    context.connectionsApi = new ConnectionsApi({ authProvider, defaultEnvironmentId: targetId });
  }
  if (context.powerPagesApi) {
    context.powerPagesApi = new PowerPagesApi({ authProvider, defaultEnvironmentId: targetId });
  }
  if (context.copilotEvaluationApi) {
    context.copilotEvaluationApi = new CopilotEvaluationApi({
      authProvider,
      defaultEnvironmentId: targetId,
    });
  }
  if (context.copilotStudioExecutionApi) {
    context.copilotStudioExecutionApi = new CopilotStudioExecutionApi({
      authProvider,
      defaultEnvironmentId: targetId,
    });
  }
  if (context.copilotAdminApi) {
    context.copilotAdminApi = new CopilotAdminApi({
      authProvider,
      defaultEnvironmentId: targetId,
    });
  }

  // Dataverse is per-environment at the URL level — re-resolve, and rebuild
  // the trio whenever the feature is on (even if resolution failed at boot;
  // the new environment may resolve fine).
  const needsDataverse =
    hasFeature(context.enabledFeatures, "dataverse") ||
    hasFeature(context.enabledFeatures, "powerPages");
  let dataverseNote: string;
  let dataverseUrl: string | null = null;
  if (needsDataverse) {
    const resolved = await resolveDataverseUrl(context, environments, target);
    if (resolved.url) {
      dataverseUrl = resolved.url;
      context.dataverseApi = new DataverseApi({ authProvider, environmentUrl: resolved.url });
      context.modelDrivenApi = new ModelDrivenApi({ authProvider, environmentUrl: resolved.url });
      context.desktopFlowsApi = new DesktopFlowsApi({ authProvider, environmentUrl: resolved.url });
      dataverseNote = `Dataverse: ${resolved.url} (resolved via ${resolved.source})`;
    } else {
      context.dataverseApi = null;
      context.modelDrivenApi = null;
      context.desktopFlowsApi = null;
      dataverseNote =
        "Dataverse: no instance URL found — Dataverse tools will return guidance here";
    }
  } else {
    dataverseNote = "Dataverse: not enabled in this deployment's feature set";
  }

  context.defaultEnvironmentId = targetId;
  context.environmentSession = {
    ...session,
    currentId: targetId,
    currentName: targetName,
    dataverseUrl,
  };

  logger.info(
    { from: session.currentId, to: targetId, dataverseUrl },
    "Session environment switched",
  );

  const headline = returningToDefault
    ? `Back on your default environment: "${targetName}" (${targetId}).`
    : `Switched to environment "${targetName}" (${targetId}) for this session.`;
  const scopeLine = returningToDefault
    ? "This matches your saved configuration."
    : `Session-only: nothing was written to your configuration — restarting the server returns to "${session.bootName}".`;

  return {
    text: [headline, "", `- ${dataverseNote}`, `- ${scopeLine}`].join("\n"),
    structured: {
      switched: !returningToDefault,
      environmentId: targetId,
      environmentName: targetName,
      dataverseUrl,
      defaultEnvironmentId: session.bootId,
      sessionOnly: true,
    },
  };
}
